import React, { useMemo } from 'react';
import { Spin, Table, Empty } from 'antd-v5';
import moment from 'moment';
import { LoadingOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { Button } from 'antd-v5';
import '../today.css';

const TodaySalesOpportunities = props => {
  // Get stage badge class based on position in pipeline (early/middle/late)
  const getStageBadgeClass = record => {
    const pipelineStages = props.salesStages.filter(
      stage => stage.sales_pipeline_id === record.sales_pipeline_id,
    );

    if (pipelineStages.length === 0) {
      return 'in-progress';
    }

    // Sort stages by weight (lower weight = earlier in pipeline)
    const sortedStages = [...pipelineStages].sort((a, b) => a.weight - b.weight);
    const stageIndex = sortedStages.findIndex(s => s.id === record.sales_opp_stage_id);

    if (stageIndex === -1) {
      return 'in-progress';
    }

    // Calculate progress ratio (0 to 1)
    const progressRatio = sortedStages.length > 1 ? stageIndex / (sortedStages.length - 1) : 0;

    // Assign color based on position in pipeline
    if (progressRatio <= 0.33) {
      return 'assigned'; // Blue - early stage (first third)
    }
    if (progressRatio <= 0.66) {
      return 'in-progress'; // Orange - middle stage (second third)
    }
    return 'active'; // Green - late stage (final third, close to closing)
  };

  const fullNameCheck = (first, last) => {
    if (last) {
      return `${first} ${last}`.trim();
    }
    return first;
  };

  const columns = useMemo(
    () => [
      {
        title: 'Due Date',
        dataIndex: 'close_date',
        key: 'close_date',
        width: '15%',
        render: t => {
          if (t) {
            return moment(t, 'YYYYMDD').format('MM/DD/YYYY');
          }
          return null;
        },
      },
      {
        title: 'Client Name',
        dataIndex: 'client_name',
        key: 'client_name',
        width: '15%',
        render: (text, record) => (
          <Link to={`/Clients?client=${record.client_id}`}>{text}</Link>
        ),
      },
      {
        title: 'Description',
        dataIndex: 'name',
        key: 'name',
        width: '25%',
        render: (text, record) => (
          <Link to={`/Clients?client=${record.client_id}&oppId=${record.id}`}>{text}</Link>
        ),
      },
      {
        title: 'Price Value',
        dataIndex: 'amount',
        key: 'amount',
        width: '10%',
        render: amount => {
          if (amount) {
            return `$${amount}`;
          }
          return '$0';
        },
      },
      {
        title: 'Stage',
        dataIndex: 'stage_name',
        key: 'stage_name',
        width: '10%',
        render: (text, record) => (
          <span className={`today-status-badge ${getStageBadgeClass(record)}`}>
            {text}
          </span>
        ),
      },
      {
        title: 'Created by',
        dataIndex: 'created_by',
        key: 'created_by',
        width: '15%',
        render: (_, record) => fullNameCheck(record.first_name, record.last_name),
      },
      {
        title: '',
        dataIndex: 'status',
        key: 'status',
        width: '10%',
        align: 'center',
        render: (_, record) => {
          const now = moment(new Date());
          if (!record.complete_date) {
            if (moment(now).isAfter(moment(record.close_date, 'YYYYMMDD'))) {
              return (
                <span className="today-status-badge overdue">
                  OVERDUE
                </span>
              );
            }
          }
          return null;
        },
      },
    ],
    [props.salesStages, props.data],
  );

  return (
    <div className="m-btm-35">
      <div className="flex-row-spacebetween-nowrap m-btm-10">
        <span className="fs-15 fw-500">{props.salesName}</span>
        <Link to="/all-sales-opp" id="ViewAllOpportunities">
          <Button type="link" className="cb-link-btn">
            View All
          </Button>
        </Link>
      </div>

      {props.isLoading ? (
        <div className="w-100-P flex-col-center m-top-10">
          <Spin tip="Loading..." size="large" indicator={<LoadingOutlined spin />}>
            <div
              style={{
                padding: 50,
                background: 'rgba(0, 0, 0, 0.1)',
                borderRadius: 8,
              }}
            />
          </Spin>
        </div>
      ) : (
        <Table
          className="today-table"
          columns={columns}
          dataSource={props.data}
          rowKey={record => record.id}
          pagination={false}
          locale={{
            emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={`No ${props.salesName}`} />,
          }}
        />
      )}
    </div>
  );
};

export default TodaySalesOpportunities;
